import React, { useEffect, useState, useCallback } from "react";
import { auth, db } from "../firebase-config";
import { workSessionService } from "../services/workSessionService";
import { collection, getDocs, where, query } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import "./EmployeeHistory.css";

function EmployeeHistoryStart() {
  const [sessions, setSessions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState('current');
  const [sites, setSites] = useState([]);
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();

  const fetchHistory = useCallback(async (email) => {
    try {
      const sessionData = await workSessionService.getEmployeeHistory(email);
      setSessions(sessionData);
    } catch (error) {
      console.error("Erreur lors de la récupération de l'historique :", error);
    }
  }, []);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchHistory(user.email);
        fetchSites();
        fetchEmployeeName(user.email);
      }
    });
    return () => unsubscribe();
  }, [fetchHistory]);

  const fetchEmployeeName = async (email) => {
    try {
      // Utiliser une requête qui respecte les règles Firestore
      const employeeQuery = query(
        collection(db, "employees"),
        where("email", "==", email)
      );
      const employeeSnapshot = await getDocs(employeeQuery);
      
      if (!employeeSnapshot.empty) {
        const employee = employeeSnapshot.docs[0];
        const employeeData = employee.data();
        const correctName = `${employeeData.prenom} ${employeeData.nom}`;
        console.log(`[EmployeeHistoryStart] Nom récupéré: "${correctName}"`);
        console.log(`[EmployeeHistoryStart] Document ID: ${employee.id}`);
        setEmployeeName(correctName);
      } else {
        console.warn(`[EmployeeHistoryStart] Aucun employé trouvé pour: ${email}`);
      }
      
    } catch (error) {
      console.error("Erreur lors de la récupération du nom de l'employé:", error);
    }
  };

  const fetchSites = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "sites"));
      const sitesList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data()
      }));
      setSites(sitesList);
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
    }
  };

  const formatDate = (timestamp) => {
    try {
      if (timestamp?.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
      }
      return "N/A";
    } catch (error) {
      console.warn('Erreur lors du formatage de la date:', error);
      return "N/A";
    }
  };

  const formatTimeHHMM = (timestamp) => {
    try {
      if (timestamp?.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleTimeString('fr-FR', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return "N/A";
    } catch (error) {
      console.warn('Erreur lors du formatage de l\'heure:', error);
      return "N/A";
    }
  };

  const formatTime = (milliseconds) => {
    if (!milliseconds) return "0h 0m";
    const totalMinutes = Math.round(milliseconds / 60000); // Arrondi à la minute la plus proche
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const getFilteredSessions = () => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return sessions.filter(session => {
      try {
        if (!session.startTime?.seconds) return false;
        const sessionDate = new Date(session.startTime.seconds * 1000);
        const sessionMonth = sessionDate.getMonth();
        const sessionYear = sessionDate.getFullYear();
        
        if (selectedMonth === 'current') {
          return sessionMonth === currentMonth && sessionYear === currentYear;
        } else {
          const previousMonth = currentMonth === 0 ? 11 : currentMonth - 1;
          const previousYear = currentMonth === 0 ? currentYear - 1 : currentYear;
          return sessionMonth === previousMonth && sessionYear === previousYear;
        }
      } catch (error) {
        console.warn('Erreur lors du traitement de la session:', session, error);
        return false;
      }
    }).sort((a, b) => {
      try {
        return (b.startTime?.seconds || 0) - (a.startTime?.seconds || 0);
      } catch (error) {
        console.warn('Erreur lors du tri des sessions:', error);
        return 0;
      }
    });
  };

  const getSiteNames = (siteIds) => {
    if (!siteIds) return "N/A";
    const siteIdArray = Array.isArray(siteIds) ? siteIds : [siteIds];
    return siteIdArray
      .map(siteId => {
        const site = sites.find(s => s.id === siteId);
        return site ? site.nom : "Site inconnu";
      })
      .join(", ");
  };

  const filteredSessions = getFilteredSessions();

  // Logs pour analyser les calculs de totaux
  useEffect(() => {
    if (filteredSessions.length > 0) {
      console.log('🔍 === ANALYSE DES CALCULS DE TOTAUX ===');
      console.log(`📊 Nombre de sessions filtrées: ${filteredSessions.length}`);
      
      let totalWorkTimeMs = 0;
      let totalPauseTimeMs = 0;
      let totalWorkTimeMsByLine = 0;
      let totalPauseTimeMsByLine = 0;
      
      console.log('📋 Détail session par session:');
      
      filteredSessions.forEach((session, index) => {
        // Calcul du temps de travail pour cette session
        let sessionWorkTimeMs = 0;
        if (session.adjustedWorkTime != null) {
          sessionWorkTimeMs = session.adjustedWorkTime;
          console.log(`  Session ${index + 1} (${formatDate(session.startTime)}): adjustedWorkTime = ${sessionWorkTimeMs}ms`);
        } else if (session.endTime?.seconds && session.startTime?.seconds) {
          const rawDurationMs = (session.endTime.seconds - session.startTime.seconds) * 1000;
          const pauseTimeMs = session.totalPauseTime || 0;
          sessionWorkTimeMs = Math.max(0, rawDurationMs - pauseTimeMs);
          console.log(`  Session ${index + 1} (${formatDate(session.startTime)}):`);
          console.log(`    - Début: ${session.startTime.seconds}s`);
          console.log(`    - Fin: ${session.endTime.seconds}s`);
          console.log(`    - Durée brute: ${rawDurationMs}ms`);
          console.log(`    - Pause: ${pauseTimeMs}ms`);
          console.log(`    - Travail calculé: ${sessionWorkTimeMs}ms`);
        } else {
          console.log(`  Session ${index + 1} (${formatDate(session.startTime)}): Données manquantes`);
        }
        
        // Calcul du temps de pause pour cette session
        const sessionPauseTimeMs = session.totalPauseTime || 0;
        
        // Accumulation des totaux
        totalWorkTimeMs += sessionWorkTimeMs;
        totalPauseTimeMs += sessionPauseTimeMs;
        
        // Calcul ligne par ligne (après formatTime)
        const workTimeFormatted = formatTime(sessionWorkTimeMs);
        const pauseTimeFormatted = formatTime(sessionPauseTimeMs);
        console.log(`    - Affichage ligne: Travail=${workTimeFormatted}, Pause=${pauseTimeFormatted}`);
        
        // Conversion inverse pour comparer
        const workTimeMinutes = Math.round(sessionWorkTimeMs / 60000);
        const pauseTimeMinutes = Math.round(sessionPauseTimeMs / 60000);
        totalWorkTimeMsByLine += workTimeMinutes * 60000;
        totalPauseTimeMsByLine += pauseTimeMinutes * 60000;
      });
      
      console.log('📊 === TOTAUX CALCULÉS ===');
      console.log(`Total travail (ms): ${totalWorkTimeMs}`);
      console.log(`Total pause (ms): ${totalPauseTimeMs}`);
      console.log(`Total travail (minutes): ${Math.round(totalWorkTimeMs / 60000)}`);
      console.log(`Total pause (minutes): ${Math.round(totalPauseTimeMs / 60000)}`);
      
      console.log('📊 === TOTAUX LIGNE PAR LIGNE ===');
      console.log(`Total travail ligne par ligne (ms): ${totalWorkTimeMsByLine}`);
      console.log(`Total pause ligne par ligne (ms): ${totalPauseTimeMsByLine}`);
      console.log(`Total travail ligne par ligne (minutes): ${Math.round(totalWorkTimeMsByLine / 60000)}`);
      console.log(`Total pause ligne par ligne (minutes): ${Math.round(totalPauseTimeMsByLine / 60000)}`);
      
      // Calcul du total affiché dans le footer
      const footerWorkTimeMs = filteredSessions.reduce((total, session) =>
        total + (session.adjustedWorkTime != null
          ? session.adjustedWorkTime
          : (session.endTime?.seconds && session.startTime?.seconds)
            ? Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0))
            : 0), 0);
      
      const footerPauseTimeMs = filteredSessions.reduce((total, session) => 
        total + (session.totalPauseTime || 0), 0);
      
      console.log('📊 === TOTAUX FOOTER ===');
      console.log(`Footer travail (ms): ${footerWorkTimeMs}`);
      console.log(`Footer pause (ms): ${footerPauseTimeMs}`);
      console.log(`Footer travail (minutes): ${Math.round(footerWorkTimeMs / 60000)}`);
      console.log(`Footer pause (minutes): ${Math.round(footerPauseTimeMs / 60000)}`);
      
      console.log('🔍 === DIFFÉRENCES ===');
      console.log(`Différence travail: ${Math.round(footerWorkTimeMs / 60000) - Math.round(totalWorkTimeMs / 60000)} minutes`);
      console.log(`Différence pause: ${Math.round(footerPauseTimeMs / 60000) - Math.round(totalPauseTimeMs / 60000)} minutes`);
      
      console.log('🔍 === FIN ANALYSE ===');
    }
  }, [filteredSessions]);

  return (
    <div className="history-container">
      <button 
        className="back-button" 
        onClick={() => navigate("/dashboard-board")}
      >
        ⏪ Retour
      </button>

      <h1 className="page-title">Historique de {employeeName}</h1>

      <div className="month-buttons">
        <button 
          className={`month-button ${selectedMonth === 'current' ? 'active' : ''}`}
          onClick={() => setSelectedMonth('current')}
        >
          Mois en cours
        </button>
        <button 
          className={`month-button ${selectedMonth === 'previous' ? 'active' : ''}`}
          onClick={() => setSelectedMonth('previous')}
        >
          Mois précédent
        </button>
      </div>

      <div className="table-container" style={{ overflowX: 'auto', width: '100%' }}>
        <table className="session-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Heure de début</th>
              <th>Heure de fin</th>
              <th>Temps de travail</th>
              <th>Pause</th>
              <th>Site</th>
            </tr>
          </thead>
          <tbody>
            {filteredSessions.map((session, index) => (
              <tr key={index}>
                <td>{formatDate(session.startTime)}</td>
                <td>{formatTimeHHMM(session.startTime)}</td>
                <td>{formatTimeHHMM(session.endTime)}</td>
                <td>{formatTime(
                  session.adjustedWorkTime != null
                    ? session.adjustedWorkTime
                    : (session.endTime?.seconds && session.startTime?.seconds)
                      ? Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0))
                      : 0
                )}</td>
                <td>{formatTime(session.totalPauseTime)}</td>
                <td>{getSiteNames(session.siteDeTravail)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan="3" style={{ textAlign: "right" }}>Total :</td>
              <td>
                {formatTime(
                  filteredSessions.reduce((total, session) => {
                    const sessionWorkTimeMs = session.adjustedWorkTime != null
                      ? session.adjustedWorkTime
                      : (session.endTime?.seconds && session.startTime?.seconds)
                        ? Math.max(0, (session.endTime.seconds - session.startTime.seconds) * 1000 - (session.totalPauseTime || 0))
                        : 0;
                    // Arrondir chaque ligne comme dans l'affichage individuel
                    const roundedWorkTimeMs = Math.round(sessionWorkTimeMs / 60000) * 60000;
                    return total + roundedWorkTimeMs;
                  }, 0)
                )}
              </td>
              <td>
                {formatTime(
                  filteredSessions.reduce((total, session) => {
                    const sessionPauseTimeMs = session.totalPauseTime || 0;
                    // Arrondir chaque ligne comme dans l'affichage individuel
                    const roundedPauseTimeMs = Math.round(sessionPauseTimeMs / 60000) * 60000;
                    return total + roundedPauseTimeMs;
                  }, 0)
                )}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

export default EmployeeHistoryStart; 